import { defineMessages } from '../define';

/**
 * The inbox stream, its filter rail and the message inspector.
 *
 * Message *content* is never in here: the text, sender names and previews are
 * captured from LINE and belong to the operator's data, not to the interface.
 */
export const inbox = defineMessages({
  en: {
    'inbox.title': 'Inbox',
    'inbox.description': 'Every message captured across your sessions, newest first.',
    'inbox.searchPlaceholder': 'Search message text…',
    'inbox.message': 'Message',

    'inbox.directionLabel': 'Direction',
    'inbox.direction.any': 'Any direction',
    'inbox.direction.INBOUND': 'Inbound',
    'inbox.direction.OUTBOUND': 'Outbound',

    'inbox.typeLabel': 'Type',
    'inbox.clearTypes': 'Clear types',

    'inbox.type.TEXT': 'Text',
    'inbox.type.STICKER': 'Sticker',
    'inbox.type.IMAGE': 'Image',
    'inbox.type.VIDEO': 'Video',
    'inbox.type.AUDIO': 'Voice message',
    'inbox.type.FILE': 'File',
    'inbox.type.LOCATION': 'Location',
    'inbox.type.CONTACT': 'Contact card',
    'inbox.type.CALL': 'Call',
    'inbox.type.SYSTEM': 'System notice',
    'inbox.type.RECALLED': 'Unsent',
    'inbox.type.FLEX': 'Rich message',
    'inbox.type.UNKNOWN': 'Unrecognised',

    'inbox.date': 'Date',
    'inbox.dateSet': 'Date set',
    'inbox.preset.hour': 'Last hour',
    'inbox.preset.today': 'Today',
    'inbox.preset.week': 'Last 7 days',
    'inbox.from': 'From',
    'inbox.to': 'To',
    'inbox.clearDates': 'Clear dates',
    'inbox.attachmentsOnly': 'Only with attachments',

    'inbox.rateHint':
      'Messages arriving per minute, averaged over the last {minutes} minutes',
    'inbox.perMinute': 'msg/min',
    'inbox.stream.pause': 'Pause the live stream',
    'inbox.stream.resume': 'Resume the live stream',
    'inbox.stream.paused': 'Paused',
    'inbox.stream.live': 'Live',
    'inbox.new.one': '{count} new message',
    'inbox.new.other': '{count} new messages',

    'inbox.export.ready': 'Export ready as {format}',
    'inbox.export.failed': 'Could not build the export',

    'inbox.inspector.empty': 'Select a message to inspect it.',
    'inbox.hint.use': 'Use',
    'inbox.hint.move': 'to move,',
    'inbox.hint.open': 'to open,',
    'inbox.hint.close': 'to close.',

    'inbox.empty.title': 'No messages captured yet',
    'inbox.empty.description':
      'Messages appear here the moment a connected session receives one.',
    'inbox.empty.goToSessions': 'Go to sessions',
    'inbox.empty.filtered.title': 'No messages match these filters',
    'inbox.empty.filtered.description':
      'Widen the date range or clear a filter to see the rest of the stream.',

    'inbox.unknownChat': 'Unknown chat',
    'inbox.unknownSender': 'Unknown',
    'inbox.thisAccount': 'This account',
    'inbox.sentiment': 'AI sentiment: {sentiment}',

    'inbox.body.unsent': 'Unsent',
    'inbox.body.location': 'Shared a location',
    'inbox.body.noText': 'No text captured',

    'inbox.field.chat': 'Chat',
    'inbox.field.sender': 'Sender',
    'inbox.field.sent': 'Sent',
    'inbox.field.received': 'Received',
    'inbox.field.unsent': 'Unsent',

    'inbox.detail.approximate': 'approximate',
    'inbox.detail.inexactTime':
      'LINE only showed a time to the minute for this message, so the seconds are our best estimate of when it was sent.',
    'inbox.detail.noText': 'No text was captured for this message.',
    'inbox.detail.download': 'Download {name}',
    'inbox.detail.copyHash': 'Copy message hash',
    'inbox.detail.hashHint':
      'SHA-256 of the normalised message. Two captures of the same message share it.',
    'inbox.detail.showMarkup': 'Show captured markup',
    'inbox.detail.hideMarkup': 'Hide captured markup',
    'inbox.detail.markupPruned':
      'The markup for this message has been pruned by the retention job.',

    'inbox.section.text': 'Text',
    'inbox.section.attachments': 'Attachments ({count})',
    'inbox.section.keywords': 'Keyword hits',
    'inbox.section.identity': 'Identity',
    'inbox.section.rawHtml': 'Raw HTML',

    'inbox.ai.run': 'Run analysis',
    'inbox.ai.finished': 'Analysis finished',
    'inbox.ai.failed': 'Could not run the analysis',
    'inbox.ai.sentiment': 'Sentiment',
    'inbox.ai.intent': 'Intent',
    'inbox.ai.category': 'Category',
    'inbox.ai.spamScore': 'Spam score',
    'inbox.ai.summary': 'Summary',
    'inbox.ai.empty':
      'This message has not been analysed. Run it to get sentiment, intent and a spam score.',
  },
  th: {
    'inbox.title': 'กล่องข้อความ',
    'inbox.description': 'ทุกข้อความที่เก็บได้จากทุกเซสชัน เรียงจากใหม่ที่สุด',
    'inbox.searchPlaceholder': 'ค้นหาข้อความ…',
    'inbox.message': 'ข้อความ',

    'inbox.directionLabel': 'ทิศทาง',
    'inbox.direction.any': 'ทุกทิศทาง',
    'inbox.direction.INBOUND': 'ขาเข้า',
    'inbox.direction.OUTBOUND': 'ขาออก',

    'inbox.typeLabel': 'ประเภท',
    'inbox.clearTypes': 'ล้างประเภท',

    'inbox.type.TEXT': 'ข้อความ',
    'inbox.type.STICKER': 'สติกเกอร์',
    'inbox.type.IMAGE': 'รูปภาพ',
    'inbox.type.VIDEO': 'วิดีโอ',
    'inbox.type.AUDIO': 'ข้อความเสียง',
    'inbox.type.FILE': 'ไฟล์',
    'inbox.type.LOCATION': 'ตำแหน่งที่ตั้ง',
    'inbox.type.CONTACT': 'นามบัตร',
    'inbox.type.CALL': 'การโทร',
    'inbox.type.SYSTEM': 'ข้อความจากระบบ',
    'inbox.type.RECALLED': 'ยกเลิกการส่ง',
    'inbox.type.FLEX': 'ข้อความ Flex',
    'inbox.type.UNKNOWN': 'ไม่ทราบประเภท',

    'inbox.date': 'วันที่',
    'inbox.dateSet': 'ตั้งวันที่แล้ว',
    'inbox.preset.hour': '1 ชั่วโมงล่าสุด',
    'inbox.preset.today': 'วันนี้',
    'inbox.preset.week': '7 วันล่าสุด',
    'inbox.from': 'ตั้งแต่',
    'inbox.to': 'ถึง',
    'inbox.clearDates': 'ล้างวันที่',
    'inbox.attachmentsOnly': 'เฉพาะที่มีไฟล์แนบ',

    'inbox.rateHint': 'จำนวนข้อความที่เข้ามาต่อนาที เฉลี่ยจาก {minutes} นาทีล่าสุด',
    'inbox.perMinute': 'ข้อความ/นาที',
    'inbox.stream.pause': 'หยุดสตรีมสดชั่วคราว',
    'inbox.stream.resume': 'ดูสตรีมสดต่อ',
    'inbox.stream.paused': 'หยุดชั่วคราว',
    'inbox.stream.live': 'สด',
    'inbox.new.one': 'ข้อความใหม่ {count} รายการ',
    'inbox.new.other': 'ข้อความใหม่ {count} รายการ',

    'inbox.export.ready': 'ไฟล์ส่งออกพร้อมแล้วในรูปแบบ {format}',
    'inbox.export.failed': 'สร้างไฟล์ส่งออกไม่สำเร็จ',

    'inbox.inspector.empty': 'เลือกข้อความเพื่อดูรายละเอียด',
    'inbox.hint.use': 'กด',
    'inbox.hint.move': 'เพื่อเลื่อน,',
    'inbox.hint.open': 'เพื่อเปิด,',
    'inbox.hint.close': 'เพื่อปิด',

    'inbox.empty.title': 'ยังไม่มีข้อความที่เก็บไว้',
    'inbox.empty.description': 'ข้อความจะปรากฏที่นี่ทันทีที่เซสชันที่เชื่อมต่ออยู่ได้รับข้อความ',
    'inbox.empty.goToSessions': 'ไปที่หน้าเซสชัน',
    'inbox.empty.filtered.title': 'ไม่มีข้อความที่ตรงกับตัวกรองนี้',
    'inbox.empty.filtered.description': 'ลองขยายช่วงวันที่หรือล้างตัวกรองเพื่อดูข้อความที่เหลือ',

    'inbox.unknownChat': 'ไม่ทราบแชท',
    'inbox.unknownSender': 'ไม่ทราบผู้ส่ง',
    'inbox.thisAccount': 'บัญชีนี้',
    'inbox.sentiment': 'อารมณ์ที่ AI วิเคราะห์ได้: {sentiment}',

    'inbox.body.unsent': 'ยกเลิกการส่งแล้ว',
    'inbox.body.location': 'แชร์ตำแหน่งที่ตั้ง',
    'inbox.body.noText': 'ไม่มีข้อความที่เก็บได้',

    'inbox.field.chat': 'แชท',
    'inbox.field.sender': 'ผู้ส่ง',
    'inbox.field.sent': 'ส่งเมื่อ',
    'inbox.field.received': 'รับเมื่อ',
    'inbox.field.unsent': 'ยกเลิกเมื่อ',

    'inbox.detail.approximate': 'โดยประมาณ',
    'inbox.detail.inexactTime':
      'LINE แสดงเวลาของข้อความนี้ละเอียดแค่ระดับนาที วินาทีที่เห็นจึงเป็นค่าประมาณที่ใกล้เคียงที่สุดของเวลาที่ส่ง',
    'inbox.detail.noText': 'ข้อความนี้ไม่มีเนื้อหาตัวอักษรที่เก็บได้',
    'inbox.detail.download': 'ดาวน์โหลด {name}',
    'inbox.detail.copyHash': 'คัดลอก hash ของข้อความ',
    'inbox.detail.hashHint':
      'ค่า SHA-256 ของข้อความที่จัดรูปแบบให้เป็นมาตรฐานแล้ว ข้อความเดียวกันที่เก็บซ้ำจะได้ค่านี้เหมือนกัน',
    'inbox.detail.showMarkup': 'แสดงมาร์กอัปที่เก็บไว้',
    'inbox.detail.hideMarkup': 'ซ่อนมาร์กอัปที่เก็บไว้',
    'inbox.detail.markupPruned': 'มาร์กอัปของข้อความนี้ถูกลบไปแล้วตามรอบการล้างข้อมูล',

    'inbox.section.text': 'เนื้อหา',
    'inbox.section.attachments': 'ไฟล์แนบ ({count})',
    'inbox.section.keywords': 'คำสำคัญที่พบ',
    'inbox.section.identity': 'ตัวระบุข้อความ',
    'inbox.section.rawHtml': 'HTML ต้นฉบับ',

    'inbox.ai.run': 'เริ่มวิเคราะห์',
    'inbox.ai.finished': 'วิเคราะห์เสร็จแล้ว',
    'inbox.ai.failed': 'ไม่สามารถวิเคราะห์ได้',
    'inbox.ai.sentiment': 'อารมณ์',
    'inbox.ai.intent': 'เจตนา',
    'inbox.ai.category': 'หมวดหมู่',
    'inbox.ai.spamScore': 'คะแนนสแปม',
    'inbox.ai.summary': 'สรุป',
    'inbox.ai.empty':
      'ข้อความนี้ยังไม่ได้วิเคราะห์ กดเริ่มวิเคราะห์เพื่อดูอารมณ์ เจตนา และคะแนนสแปม',
  },
});
